import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  ValidationError
} from '@/lib/excel';

const HEADERS = {
  MA_VT: 'Mã vật tư*',
  TEN_VT: 'Tên vật tư',
  DVT: 'Đơn vị tính*',
  NGAY_HL: 'Ngày hiệu lực*',
  NHA_CUNG_CAP: 'Nhà cung cấp',
  TEN_KHACH_HANG: 'Tên khách hàng',
  NGOAI_TE: 'Ngoại tệ*',
  SO_LUONG_TU: 'Số lượng từ',
  GIA_MUA: 'Giá mua'
};

const HEADER_CONFIG: HeaderConfig[] = [
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'main' },
  { header: HEADERS.TEN_VT, key: 'ten_vt', type: 'main' },
  { header: HEADERS.DVT, key: 'dvt', type: 'main' },
  { header: HEADERS.NGAY_HL, key: 'ngay_hl', type: 'main' },
  { header: HEADERS.NHA_CUNG_CAP, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KHACH_HANG, key: 'ten_kh', type: 'main' },
  { header: HEADERS.NGOAI_TE, key: 'ma_nt', type: 'main' },
  { header: HEADERS.SO_LUONG_TU, key: 'sl_tu', type: 'main' },
  { header: HEADERS.GIA_MUA, key: 'gia_nt', type: 'main' }
];

const UUID_FIELDS = {
  main: ['ma_vt', 'dvt', 'ma_kh', 'ma_nt']
};

type UUIDLookupResponse = Record<string, Record<string, Array<string | null | undefined>> | undefined>;

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const parseNum = (val: unknown, def = 0) => {
  if (val == null || String(val).trim() === '') return def;
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
};

const getVal = (
  uuid: UUIDLookupResponse | undefined,
  parsed: Record<string, unknown[]>,
  section: string,
  field: string,
  i: number
) => (uuid?.[section]?.[field]?.[i] || parsed[field]?.[i] || undefined) as string | undefined;

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, unknown[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section[f] || []]));

  return {
    main: build(data.main || {}, UUID_FIELDS.main)
  };
};

const ValidateUUID = (
  response: UUIDLookupResponse | null | undefined,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  const data = response.main;
  if (!data) return errors;

  Object.entries(data).forEach(([field, values]) => {
    if (!Array.isArray(values)) return;
    const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === 'main');

    values.forEach((val, i) => {
      if (val === null) {
        errors.push({
          row: i + 2,
          field,
          message: `${getFieldName(field)} không hợp lệ`,
          section: 'main',
          columnIndex: mapping?.index
        });
      }
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  uuidResponse: UUIDLookupResponse | undefined,
  _context: unknown
): ImportBody[] => {
  const { main = {} } = parsedData;
  const bodies: ImportBody[] = [];

  for (let i = 0; i < dataRows.length; i++) {
    const ma_vt = getVal(uuidResponse, main, 'main', 'ma_vt', i);
    const dvt = getVal(uuidResponse, main, 'main', 'dvt', i);
    const ngay_hl = convertExcelDate(main.ngay_hl?.[i]);
    const ma_nt = getVal(uuidResponse, main, 'main', 'ma_nt', i);
    const ma_kh = getVal(uuidResponse, main, 'main', 'ma_kh', i);
    const sl_tu = parseNum(main.sl_tu?.[i], 0);
    const gia_nt = parseNum(main.gia_nt?.[i], 0);

    // Skip rows without required fields
    if (!ma_vt || !dvt || !ma_nt || !ngay_hl) {
      continue;
    }

    bodies.push({
      ma_vt,
      dvt,
      ngay_hl,
      ma_nt,
      ma_kh: ma_kh || undefined,
      sl_tu,
      gia_nt,
      status: 1
    });
  }

  return bodies;
};

export interface ImportBody {
  ma_vt: string;
  dvt: string;
  ngay_hl: string;
  ma_nt: string;
  ma_kh?: string;
  sl_tu: number;
  gia_nt: number;
  status: number;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  RequiredFields: [
    { key: 'ma_vt', type: 'main', label: 'Mã vật tư' },
    { key: 'dvt', type: 'main', label: 'Đơn vị tính' },
    { key: 'ngay_hl', type: 'main', label: 'Ngày hiệu lực' },
    { key: 'ma_nt', type: 'main', label: 'Ngoại tệ' }
  ],
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
